/**
 * ARCHITECTURE CONTRACT:
 * - Only source of truth for session bootstrap
 * - Must remain single-flight (sessionInitPromise)
 * - Must be triggered only from router or explicit restore flow
 * - DO NOT introduce new bootstrap entrypoints
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { clearSuperAdmin, setSuperAdmin, setToken as setClientToken, clearToken as clearClientToken } from '../api/client.js'
import {
  login as loginRequest,
  getMe,
  changePassword as changePasswordRequest,
  requestPasswordReset as requestPasswordResetRequest,
  resetPassword as resetPasswordRequest,
} from '../api/auth.ts'
import { AUTH_ERROR_CODE } from '../api/authContract'
import { useOrgStore } from './orgStore.js'
import { hasRole } from '../utils/permissions.js'
import { evaluateUserContext } from '../domain/model.js'

let sessionInitPromise = null

export const useAuthStore = defineStore('auth', () => {
  const orgStore = useOrgStore()
  const token = ref(localStorage.getItem('access_token') || '')
  const must_change_password = ref(false)
  const user = ref(null)
  const isLoading = ref(false)
  const loading = computed(() => isLoading.value)
  const error = ref('')
  const sessionInvalidationReason = ref('')
  const trace = ref([])
  const runId = ref(0)
  const status = ref('idle')
  const rbacInitializing = ref(false)
  const rbacReady = ref(false)
  const contextReady = computed(() => rbacReady.value && orgStore.contextReady)

  const role = computed(() => user.value?.role ?? '')
  const userId = computed(() => user.value?.id ?? null)

  const isAuthenticated = computed(() => Boolean(token.value))
  const isSuperAdmin = computed(() => user.value?.is_superadmin === true)
  const isAdmin = computed(() => hasRole({ user: user.value, isSuperAdmin: isSuperAdmin.value }, orgStore, 'admin'))
  const isManager = computed(() => hasRole({ user: user.value, isSuperAdmin: isSuperAdmin.value }, orgStore, 'manager'))
  const isUser = computed(() => hasRole({ user: user.value, isSuperAdmin: isSuperAdmin.value }, orgStore, 'user'))
  const roles = computed(() => {
    const resolved = orgStore.activeOrgRole ?? null
    return resolved ? [String(resolved).toLowerCase()] : []
  })
  const permissions = computed(() => {
    const evaluated = evaluateUserContext(
      user.value,
      {
        activeOrgId: orgStore.activeOrgId,
        activeOrgRole: orgStore.activeOrgRole,
        activeSubOrgId: orgStore.activeSubOrgId,
        organizations: orgStore.organizations,
      },
      {
        lotteries: [],
        accessibleLotteryIds: [],
      },
    )

    return Object.entries(evaluated.permissions)
      .filter(([, allowed]) => allowed === true)
      .map(([action]) => action)
  })

  const log = (step) => {
    const msg = `${Date.now()} | ${step}`
    trace.value.push(msg)
    if (import.meta.env.DEV) {
      console.log('[AUTH TRACE]', msg)
    }
  }

  const debugSnapshot = () => {
    return {
      status: status.value,
      hasToken: Boolean(token.value),
      hasUser: Boolean(user.value),
      isSuperAdmin: isSuperAdmin.value,
      roles: roles.value,
      permissions: permissions.value,
    }
  }

  const syncUserFromToken = () => {
    if (!token.value) {
      user.value = null
      setSuperAdmin(false)
      return
    }

    if (!user.value) {
      user.value = {
        id: null,
        role: '',
        is_superadmin: false,
      }
    }

    setSuperAdmin(user.value?.is_superadmin === true)
  }

  const setToken = (t) => {
    log('TOKEN_SET_START')
    token.value = t
    setClientToken(t)
    syncUserFromToken()
    status.value = token.value ? 'authenticated_token' : 'idle'
    log('TOKEN_SET_DONE')
  }

   const clearToken = (skipStatusReset = false) => {
     log('TOKEN_CLEAR_START')
     token.value = ''
     clearClientToken()
     syncUserFromToken()
     if (!skipStatusReset) {
       status.value = 'idle'
     }
     log('TOKEN_CLEAR_DONE')
   }

  const login = async (username, password) => {
    const id = ++runId.value
    log(`LOGIN_START run=${id}`)
    isLoading.value = true
    error.value = ''
    status.value = 'authenticating'

    try {
      const response = await loginRequest(username, password)
      log(`LOGIN_OK run=${id}`)

      if (id !== runId.value) {
        log(`LOGIN_ABORTED stale run=${id}`)
        return { success: false, error: 'Stale login run' }
      }

      if (!response.success) {
        error.value = response.error.message
        status.value = 'error'
        log(`LOGIN_ERROR run=${id}`)
        return { success: false, error: error.value }
      }

      const t = response.data?.access_token
      if (!t) {
        error.value = 'Login response did not include access token'
        status.value = 'error'
        return { success: false, error: error.value }
      }

      log(`TOKEN_SET run=${id}`)
      setToken(t)

      must_change_password.value = response.data?.must_change_password ?? false
      log(`USER_FETCH_BEFORE run=${id}`)

      const sessionResult = await initializeSession({ runId: id, source: 'login' })

      if (id !== runId.value) {
        log(`LOGIN_ABORTED stale run=${id}`)
        return { success: false, error: 'Stale login run' }
      }

      if (!sessionResult.success) {
        status.value = 'error'
        return sessionResult
      }

      status.value = 'authenticated'
      log(`STATUS_AUTHENTICATED run=${id}`)

      return { success: true, must_change_password: must_change_password.value }
    } finally {
      isLoading.value = false
      log(`LOGIN_END run=${id}`)
    }
  }

  const changePassword = async (old_password, new_password) => {
    isLoading.value = true
    error.value = ''

    try {
      const response = await changePasswordRequest(old_password, new_password)

      if (!response.success) {
        error.value = response.error.message
        return { success: false, error: error.value }
      }

      must_change_password.value = false
      return { success: true }
    } finally {
      isLoading.value = false
    }
  }

  const fetchUser = async () => {
    log('USER_FETCH_START')
    const response = await getMe()

    // CRITICAL: Check for session invalidation on authenticated endpoints
    // Per auth-contract.md: if authenticated endpoint returns 403,
    // treat as session invalidation (org/suborg became deleted, or access revoked)
    if (!response.success && response.error?.isSessionInvalidating) {
      log(`USER_FETCH_SESSION_INVALIDATED code=${response.error.code}`)
      return {
        success: false,
        error: response.error.message,
        sessionInvalidated: true,
        errorCode: response.error.code,
      }
    }

    if (!response.success) {
      log('USER_FETCH_ERROR')
      return { success: false, error: response.error.message }
    }

    user.value = response.data
    setSuperAdmin(user.value?.is_superadmin === true)

    if (import.meta.env.DEV) {
      console.log('[AUTH DEBUG]', {
        isSuperAdmin: isSuperAdmin.value,
        user: user.value,
      })
    }
    log('USER_LOADED')
    return { success: true, data: response.data }
  }

  const fetchMe = fetchUser

  const requestPasswordReset = async (email) => {
    isLoading.value = true
    error.value = ''

    try {
      const response = await requestPasswordResetRequest(email)
      if (!response.success) {
        error.value = response.error.message
        return { success: false, error: error.value }
      }

      return { success: true, data: response.data }
    } finally {
      isLoading.value = false
    }
  }

  const resetPassword = async (resetToken, newPassword) => {
    isLoading.value = true
    error.value = ''

    try {
      const response = await resetPasswordRequest(resetToken, newPassword)
      if (!response.success) {
        error.value = response.error.message
        return { success: false, error: error.value }
      }

      return { success: true, data: response.data }
    } finally {
      isLoading.value = false
    }
  }

  const initializeSession = async (options = {}) => {
    const contextRunId = options.runId
    log(`SESSION_INIT_START run=${contextRunId ?? 'n/a'}`)

    if (sessionInitPromise) {
      log(`SESSION_INIT_SINGLE_FLIGHT_HIT run=${contextRunId ?? 'n/a'}`)
      return sessionInitPromise
    }

    if (!token.value) {
      log('SESSION_INIT_SKIP no_token')
      return { success: false, error: 'Not authenticated' }
    }

    if (contextRunId && contextRunId !== runId.value) {
      log(`SESSION_INIT_ABORT stale run=${contextRunId}`)
      return { success: false, error: 'Stale login run' }
    }

    log(`SESSION_INIT_SINGLE_FLIGHT_START run=${contextRunId ?? 'n/a'}`)
    sessionInitPromise = (async () => {
       const fail = (stage, errorMessage) => {
         const error = errorMessage ?? 'Session initialization failed'
         log(`SESSION_INIT_FAILED stage=${stage} error=${error} run=${contextRunId ?? 'n/a'}`)
         sessionInitPromise = null
         return { success: false, error }
       }

       rbacInitializing.value = true
       rbacReady.value = false

       try {
         if (contextRunId && contextRunId !== runId.value) {
           log(`SESSION_INIT_ABORT stale run=${contextRunId}`)
           return fail('stale_run', 'Stale login run')
         }

         log(`USER_FETCH_BEFORE run=${contextRunId ?? 'n/a'}`)
         const meResult = await fetchUser()
         log(`USER_FETCH_AFTER run=${contextRunId ?? 'n/a'} success=${meResult.success}`)

         // CRITICAL: Handle session invalidation on authenticated endpoints
         if (!meResult.success && meResult.sessionInvalidated) {
           log(`SESSION_INIT_DETECTED_INVALIDATION code=${meResult.errorCode}`)
           invalidateSession(
             'Your session is no longer valid. Please contact your administrator.',
             meResult.errorCode
           )
           sessionInitPromise = null
           return fail('session_invalidated', meResult.error)
         }

         if (!meResult.success) {
           return fail('user_fetch', meResult.error)
         }

         if (contextRunId && contextRunId !== runId.value) {
           log(`SESSION_INIT_ABORT stale run=${contextRunId}`)
           return fail('stale_run', 'Stale login run')
         }

         log(`RBAC_LOAD_START run=${contextRunId ?? 'n/a'}`)
         const orgResult = await orgStore.initializeOrganizations({
           preferredSubOrgId: user.value?.sub_org_id ?? null,
         })
         log(`RBAC_LOAD_AFTER run=${contextRunId ?? 'n/a'} success=${orgResult.success}`)
         if (!orgResult.success) {
           return fail('rbac_load', orgResult.error)
         }

         log(`PERMISSIONS_RECEIVED run=${contextRunId ?? 'n/a'} count=${permissions.value.length}`)
         log(`RBAC_READY run=${contextRunId ?? 'n/a'}`)
         rbacReady.value = true
         status.value = 'session_ready'
         log(`SESSION_INIT_DONE run=${contextRunId ?? 'n/a'}`)
         sessionInitPromise = null

         return { success: true }
       } catch (err) {
         return fail('unexpected', err?.message)
       } finally {
         rbacInitializing.value = false
       }
     })()

    return sessionInitPromise
  }

  const logout = () => {
    log('LOGOUT_START')
    clearToken()
    must_change_password.value = false
    user.value = null
    clearSuperAdmin()
    rbacReady.value = false
    rbacInitializing.value = false
    orgStore.setOrganizations([])
    orgStore.setActiveOrg(null)
    status.value = 'idle'
    sessionInvalidationReason.value = ''
    log('LOGOUT_DONE')
  }

   /**
    * Invalidate session when backend denies access
    * (e.g., org/suborg became deleted, or other access revocation)
    *
    * Called when authenticated endpoint returns 403.
    * This is different from normal logout - it indicates backend state changed.
    */
   const invalidateSession = (reason = 'Session invalidated. Please log in again.', errorCode = null) => {
     log(`SESSION_INVALIDATION_START code=${errorCode}`)
     sessionInvalidationReason.value = reason
     status.value = 'session_invalidated'

     // Log which error code triggered invalidation
     if (errorCode) {
       log(`SESSION_INVALIDATION_TRIGGER code=${errorCode}`)
       if (errorCode === AUTH_ERROR_CODE.ORG_DELETED) {
         log('SESSION_INVALIDATION_REASON org_deleted')
       } else if (errorCode === AUTH_ERROR_CODE.SUBORG_DELETED) {
         log('SESSION_INVALIDATION_REASON suborg_deleted')
       } else if (errorCode === AUTH_ERROR_CODE.ACCESS_REVOKED) {
         log('SESSION_INVALIDATION_REASON access_revoked')
       }
     }

     // Clear token but preserve session_invalidated status
     clearToken(true)
     must_change_password.value = false
     user.value = null
     clearSuperAdmin()
     rbacReady.value = false
     rbacInitializing.value = false
     orgStore.setOrganizations([])
     orgStore.setActiveOrg(null)

     log('SESSION_INVALIDATION_DONE')
   }

  // ─── Startup: sync from token but do NOT auto-run initializeSession ──────────
  // App.vue onMounted is the single entrypoint for page-reload init.
  syncUserFromToken()

  return {
    token,
    must_change_password,
    user,
    role,
    userId,
    isLoading,
    loading,
    error,
    sessionInvalidationReason,
    trace,
    runId,
    status,
    roles,
    permissions,
    isAuthenticated,
    isSuperAdmin,
    isAdmin,
    isManager,
    isUser,
    log,
    debugSnapshot,
    login,
    changePassword,
    requestPasswordReset,
    resetPassword,
    logout,
    invalidateSession,
    setToken,
    fetchUser,
    fetchMe,
    initializeSession,
    rbacReady,
    rbacInitializing,
    contextReady,
  }
})

